import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  type BookingDraftScope,
  loadBookingDraft,
  saveBookingDraft,
} from "@/lib/bookingDraftStorage";

// 価格種別 ID (string化した price_type_id) ごとの人数。
export type PriceQuantities = Record<string, number>;

// 購入者情報入力画面で入力する内容。
export type BuyerInfo = {
  lastName: string;
  firstName: string;
  phoneNumber: string;
  email: string;
  // アレルギー (任意。自由記述。JPTP_CANSEI-1382)。
  allergy: string;
  // 要望 (任意。自由記述。JPTP_CANSEI-1382)。
  request: string;
  // メールアドレス認証の完了状態 (VerifyEmailCode 成功で true。§6.4)。
  emailVerified: boolean;
};

// コース詳細画面で入力する予約内容。
export type CourseBookingSelection = {
  date: string;
  time: string;
  quantities: PriceQuantities;
  agreedTerms: boolean;
  agreedPrivacy: boolean;
  agreedCancellation: boolean;
};

const initialBuyerInfo: BuyerInfo = {
  lastName: "",
  firstName: "",
  phoneNumber: "",
  email: "",
  allergy: "",
  request: "",
  emailVerified: false,
};

const initialSelection: CourseBookingSelection = {
  date: "",
  time: "",
  quantities: {},
  agreedTerms: false,
  agreedPrivacy: false,
  agreedCancellation: false,
};

type CourseBookingContextValue = {
  selection: CourseBookingSelection;
  setSelection: (selection: CourseBookingSelection) => void;
  buyerInfo: BuyerInfo;
  // 非同期処理の完了後に更新する場合は、古いスナップショットで入力内容を巻き戻さないよう
  // 更新関数を渡す (JPTP_CANSEI-1657)。
  setBuyerInfo: Dispatch<SetStateAction<BuyerInfo>>;
  // 「決済に進む」(CreateCheckoutSession) で作成した予約の ID。決済せずに戻ってきた後の再決済で
  // 同じ予約を指定し、旧 Stripe セッションを expire させるために保持する (§6.5・§7.2。JPTP_CANSEI-1678)。
  // 値は Stripe へ出る直前に markCheckoutStarted が下書きへ同期的に書き込み、復帰時に復元する。
  reservationId: string | null;
  // Stripe Checkout から決済せずに戻ってきたか (下書きの checkoutStarted を復元したか)。
  // 購入者情報入力画面で「決済が完了していません」の案内を出す判定に使う (JPTP_CANSEI-1428)。
  returnedFromCheckout: boolean;
};

const CourseBookingContext = createContext<CourseBookingContextValue | undefined>(undefined);

// 価格種別ごとの人数が同一かを比較する (キー欠落は 0 とみなす)。予約条件変更の判定に使う。
function areQuantitiesEqual(a: PriceQuantities, b: PriceQuantities): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if ((a[key] ?? 0) !== (b[key] ?? 0)) {
      return false;
    }
  }
  return true;
}

type CourseBookingProviderProps = BookingDraftScope & { children: ReactNode };

/**
 * コース詳細画面 (features/courses、予約内容の選択) と購入者情報入力画面
 * (features/reservation、購入者情報の入力) の間で入力内容を共有する Context。
 * feature 間の直接 import は禁止のため (ADR-009)、共有層 (lib/) に置き、
 * 両 feature から利用する。Provider は CourseDetailLayout (features/courses) に置く。
 *
 * 入力内容は sessionStorage にも退避し、Stripe Checkout への往復 (フルページ遷移で SPA が
 * 再起動する) をまたいで復元する (JPTP_CANSEI-1428)。復元先を誤らないよう、URL の
 * caseToken / shopId / productId を保存時のスコープとして受け取る。
 */
export function CourseBookingProvider({
  caseToken,
  shopId,
  productId,
  children,
}: CourseBookingProviderProps) {
  // 下書きの読み出しはマウント時の 1 回だけ。副作用の無い読み出しのため、StrictMode で
  // 初期化関数が二重に呼ばれても結果は変わらない。
  const [restoredDraft] = useState(() => loadBookingDraft({ caseToken, shopId, productId }));
  const [selection, setSelectionState] = useState<CourseBookingSelection>(
    restoredDraft?.selection ?? initialSelection,
  );
  const [buyerInfo, setBuyerInfo] = useState<BuyerInfo>(
    // メールアドレス認証は backend が Checkout Session 発行時に消し込むため復元しない。
    // 画面だけ認証済みに戻してもサーバ側では無効になる (§6.4)。
    restoredDraft ? { ...restoredDraft.buyerInfo, emailVerified: false } : initialBuyerInfo,
  );
  const [reservationId, setReservationId] = useState<string | null>(
    restoredDraft?.reservationId ?? null,
  );
  const returnedFromCheckout = restoredDraft?.checkoutStarted ?? false;

  // 予約条件 (利用日時・人数) が変わったら既存の予約は変更後の内容と紐づかないため無効化する。
  // reservationId とメール認証済みフラグをクリアし、古い予約 ID の再利用を防ぐ (§6.4/§6.5)。
  // 同意事項の変更では予約内容は変わらないため無効化しない。
  const setSelection = useCallback(
    (next: CourseBookingSelection) => {
      const bookingChanged =
        selection.date !== next.date ||
        selection.time !== next.time ||
        !areQuantitiesEqual(selection.quantities, next.quantities);
      setSelectionState(next);
      if (bookingChanged) {
        setReservationId(null);
        setBuyerInfo((info) => (info.emailVerified ? { ...info, emailVerified: false } : info));
      }
    },
    [selection],
  );

  // 入力のたびに下書きを退避する。checkoutStarted は復元した時点で落とし、決済せずに
  // 戻ってきた案内が次回以降も出続けないようにする (印は Stripe へ出る直前に立て直す)。
  useEffect(() => {
    saveBookingDraft({
      caseToken,
      shopId,
      productId,
      selection,
      buyerInfo: {
        lastName: buyerInfo.lastName,
        firstName: buyerInfo.firstName,
        phoneNumber: buyerInfo.phoneNumber,
        email: buyerInfo.email,
        allergy: buyerInfo.allergy,
        request: buyerInfo.request,
      },
      reservationId,
      checkoutStarted: false,
    });
  }, [caseToken, shopId, productId, selection, buyerInfo, reservationId]);

  const value = useMemo(
    () => ({
      selection,
      setSelection,
      buyerInfo,
      setBuyerInfo,
      reservationId,
      returnedFromCheckout,
    }),
    [selection, setSelection, buyerInfo, reservationId, returnedFromCheckout],
  );

  return <CourseBookingContext.Provider value={value}>{children}</CourseBookingContext.Provider>;
}

export function useCourseBookingContext(): CourseBookingContextValue {
  const context = useContext(CourseBookingContext);
  if (!context) {
    throw new Error("useCourseBookingContext must be used within a CourseBookingProvider");
  }
  return context;
}
